import { portalText, portalLocale } from "../../../i18n/portal.js";
/**
 * Create an organization — the decisions the screen makes, without React (plan-014 B1; design §27.15.1;
 * FR12-ONB-01, FR12-ONB-03, FR12-ONB-05). `account-creation-flow.tsx` renders them.
 *
 *   currentStepOf        which of the three steps the draft leaves the person on
 *   trialNoticeOf        the trial notice: "30 free minutes" when this verified person has not had one,
 *                        otherwise why not (never a trial the API would not grant)
 *   organizationProblem  why the organization step cannot be sent yet, in the screen's words
 *   attemptKeyFor        the Idempotency-Key: the same for a retry of the same form, new when it changes
 */
import { SUPPORTED_COUNTRY, isSupportedCountry, type SupportedCountry } from "@wetalk/domain";

import type { OnboardingDraftView, OnboardingStepView, TrialEligibilityView } from "../../../api/slice/account.js";

/** The organization's name, as the API bounds it (design §15.4.1). */
export const ORGANIZATION_NAME_LENGTH = { minimum: 2, maximum: 120 } as const;

/** The first workspace's name, as the API bounds it (B3's workspace-name rule). */
export const FIRST_WORKSPACE_NAME_MAXIMUM = 60;

/** The step the page shows: the first one the draft has not completed. */
export function currentStepOf(draft: Pick<OnboardingDraftView, "stepCompleted">): OnboardingStepView {
  if (!draft.stepCompleted.includes("identity")) return "identity";
  if (!draft.stepCompleted.includes("organization")) return "organization";
  return "voice_agent";
}

/** "Step 2 of 3", for the kicker. */
export function stepNumberOf(step: OnboardingStepView): number {
  return step === "identity" ? 1 : step === "organization" ? 2 : 3;
}

/** "30 free minutes" from the configured seconds — never rounded into a figure it is not. */
export function trialLengthOf(grantedSecond: number): string {
  if (grantedSecond % 60 === 0) {
    const minute = grantedSecond / 60;
    return portalText("core", "start.free_minutes", { count: minute });
  }
  return portalText("core", "start.free_seconds", { count: grantedSecond });
}

export interface TrialNotice {
  /** `accent` when this organization gets the trial; `neutral` when it does not. */
  readonly tone: "accent" | "neutral";
  readonly title: string;
  readonly detail: string;
}

/** The notice beside the organization form (the prototype's "30 free minutes" block). */
export function trialNoticeOf(eligibility: TrialEligibilityView, grantedSecond: number): TrialNotice {
  const length = trialLengthOf(grantedSecond);
  switch (eligibility) {
    case "eligible":
      return {
        tone: "accent",
        title: length,
        detail: portalText("core", "start.your_verified_account_has_not_used_a_trial_yet_so_this_organizati_8acd81"),
      };
    case "already_claimed":
    case "legacy_trial":
      return {
        tone: "neutral",
        title: portalText("core", "start.no_free_minutes_for_this_organization_a283c7"),
        detail: portalText("core", "start.a_trial_is_given_once_per_person_and_yours_went_to_an_organizatio_6cdf41"),
      };
    case "email_unverified":
      return {
        tone: "neutral",
        title: portalText("core", "start.no_free_minutes_until_your_email_is_verified_cfea35"),
        detail: portalText("core", "start.a_trial_is_given_once_per_verified_person_d7c17e"),
      };
    case "person_unknown":
      return { tone: "neutral", title: portalText("core", "start.no_free_minutes_063b1b"), detail: portalText("core", "start.we_could_not_find_your_sign_in_sign_in_again_f4574f") };
  }
}

/** The organization step's inputs as the form holds them. */
export interface OrganizationForm {
  readonly accountName: string;
  readonly country: SupportedCountry | null;
  readonly firstWorkspaceName: string;
  readonly termsAccepted: boolean;
}

/** The form a draft resumes into. "Main" is the prototype's first-workspace value. */
export function organizationFormOf(draft: OnboardingDraftView): OrganizationForm {
  const country = draft.input.country;
  return {
    accountName: draft.input.accountName ?? "",
    country: country !== null && isSupportedCountry(country) ? country : null,
    firstWorkspaceName: draft.input.firstWorkspaceName ?? portalText("core", "start.main_62bce9"),
    termsAccepted: false,
  };
}

/** Why the organization step cannot be sent yet — or `null` when it can. */
export function organizationProblem(form: OrganizationForm): string | null {
  const name = form.accountName.trim();
  if (name.length < ORGANIZATION_NAME_LENGTH.minimum) return portalText("core", "start.give_the_organization_a_name_of_at_least_two_characters_a57dfb");
  if (name.length > ORGANIZATION_NAME_LENGTH.maximum) {
    return portalText("core", "start.an_organization_name_is_at_most_characters_3063ef", { value0: String(ORGANIZATION_NAME_LENGTH.maximum) });
  }
  if (form.country === null) return portalText("core", "start.choose_the_country_the_organization_is_in_95a910");
  const workspace = form.firstWorkspaceName.trim();
  if (workspace === "") return portalText("core", "start.give_the_first_workspace_a_name_7c30cb");
  if ([...workspace].length > FIRST_WORKSPACE_NAME_MAXIMUM) {
    return portalText("core", "start.a_workspace_name_is_at_most_characters_ced2f9", { value0: String(FIRST_WORKSPACE_NAME_MAXIMUM) });
  }
  if (!form.termsAccepted) return portalText("core", "start.accept_the_terms_and_the_privacy_policy_to_create_the_organizatio_840d48");
  return null;
}

/** One submission's contents and the key that goes with them. */
export interface Attempt {
  readonly fingerprint: string;
  readonly idempotencyKey: string;
}

/**
 * The key for this submission: the held one when the form is unchanged (a retry after a failure cannot
 * create a second organization), a new one when anything changed (the old key would be refused `409`).
 */
export function attemptKeyFor(held: Attempt | null, fingerprint: string, mint: () => string): Attempt {
  return held !== null && held.fingerprint === fingerprint ? held : { fingerprint, idempotencyKey: mint() };
}

export interface CountryOption {
  readonly code: SupportedCountry;
  readonly name: string;
}

/** The closed country list with English names, sorted by name. Never a proposed default. */
export function countryOptions(): readonly CountryOption[] {
  const names = new Intl.DisplayNames([portalLocale()], { type: "region" });
  return SUPPORTED_COUNTRY.map((code) => ({ code, name: names.of(code) ?? code })).sort((left, right) =>
    left.name.localeCompare(right.name, portalLocale()),
  );
}
